import { HttpBackend, HttpClient } from "@angular/common/http";
import { inject, Inject, Injectable } from "@angular/core";
import { ENVIRONMENT } from "../environments/environments.token";
import { Environment } from "../environments/environment.interface";
import { BehaviorSubject, Observable, catchError, map, throwError } from "rxjs";
import { Cls_MBUser, Cls_MB_CreateBroadCast, Cls_MB_CreateNotificationMain } from "../models";

interface Int_HttpHeaders {
  "X-MAGICBELL-API-KEY": string;
  "X-MAGICBELL-API-SECRET": string;
  "X-MAGICBELL-USER-EXTERNAL-ID"?: string;
}

@Injectable({
  providedIn: "root",
})
export class MagicBellService {
  private env: Environment = inject(ENVIRONMENT);
  private handler: HttpBackend = inject(HttpBackend);
  private http: HttpClient = inject(HttpClient);
  private httpClient: HttpClient = new HttpClient(this.handler);

  constructor() {
    this.httpHeaders = {
      "X-MAGICBELL-API-KEY": this.MAGIC_BELL_KEY,
      "X-MAGICBELL-API-SECRET": this.MAGIC_BELL_SECRET_KEY,
    };
  }

  private readonly MAGIC_BELL_API: string = this.env.MAGIC_BELL_API || "";
  private readonly MAGIC_BELL_KEY: string = this.env.MAGIC_BELL_KEY || "";
  private readonly MAGIC_BELL_SECRET_KEY: string = this.env.MAGIC_BELL_SECRET_KEY || "";

  httpHeaders: Int_HttpHeaders | any = {
    "X-MAGICBELL-API-KEY": "",
    "X-MAGICBELL-API-SECRET": "",
  };

  //#region spinner

  private count: number = 0;
  private spinner$ = new BehaviorSubject<string>("");

  getSpinnerObserver(): Observable<string> {
    return this.spinner$.asObservable();
  }

  requestStarted() {
    if (++this.count === 1) {
      this.spinner$.next("start");
    }
  }

  requestEnded() {
    if (this.count === 0 || --this.count === 0) {
      this.spinner$.next("stop");
    }
  }

  resetSpinner() {
    this.count = 0;
    this.spinner$.next("stop");
  }

  //#endregion

  returnModifiedObserver(API: Observable<any>): Observable<any> {
    this.requestStarted();
    return API.pipe(
      map(res => {
        this.requestEnded();
        return res;
      }),
      catchError(error => {
        this.requestEnded();
        return throwError(error);
      })
    );
  }

  //#region GET

  getNotification(UserID: string, per_page: number = 10, page: number = 1, categories: string[] = ["_comment"]) {
    this.httpHeaders["X-MAGICBELL-USER-EXTERNAL-ID"] = UserID;
    const API = this.httpClient.get(`${this.MAGIC_BELL_API}/notifications?per_page=${per_page}&page=${page}&categories=${categories}`, { headers: this.httpHeaders });
    return this.returnModifiedObserver(API);
  }

  getUserByID(ID: string) {
    return this.http.get(`${this.MAGIC_BELL_API}/users/${ID}`, { headers: this.httpHeaders });
  }

  getAllUsers() {
    const API = this.httpClient.get(`${this.MAGIC_BELL_API}/users`, { headers: this.httpHeaders });
    return this.returnModifiedObserver(API);
  }

  //#endregion

  //#region POST

  createUser(payload: Cls_MBUser) {
    return this.http.post(`${this.MAGIC_BELL_API}/users`, payload, { headers: this.httpHeaders });
  }

  createBroadcast(payload: Cls_MB_CreateBroadCast) {
    const API = this.httpClient.post(`${this.MAGIC_BELL_API}/broadcasts`, payload, { headers: this.httpHeaders });
    return this.returnModifiedObserver(API);
  }

  createNotification(payload: Cls_MB_CreateNotificationMain) {
    const API = this.httpClient.post(`${this.MAGIC_BELL_API}/notifications`, payload, { headers: this.httpHeaders });
    return this.returnModifiedObserver(API);
  }

  markAllNotificationsAsSeen(UserID: string) {
    this.httpHeaders["X-MAGICBELL-USER-EXTERNAL-ID"] = UserID;
    const API = this.httpClient.post(`${this.MAGIC_BELL_API}/notifications/seen`, {}, { headers: this.httpHeaders });
    return this.returnModifiedObserver(API);
  }

  markAllNotificationsAsRead(UserID: string) {
    this.httpHeaders["X-MAGICBELL-USER-EXTERNAL-ID"] = UserID;
    const API = this.httpClient.post(`${this.MAGIC_BELL_API}/notifications/read`, {}, { headers: this.httpHeaders });
    return this.returnModifiedObserver(API);
  }

  markNotificationAsRead(notification_id: string | number) {
    const API = this.httpClient.post(`${this.MAGIC_BELL_API}/notifications/${notification_id}/read`, {}, { headers: this.httpHeaders });
    return this.returnModifiedObserver(API);
  }

  markNotificationAsUnRead(notification_id: string | number) {
    const API = this.httpClient.post(`${this.MAGIC_BELL_API}/notifications/${notification_id}/unread`, {}, { headers: this.httpHeaders });
    return this.returnModifiedObserver(API);
  }

  // pushSubscription(UserID: string) {
  //   this.httpHeaders["X-MAGICBELL-USER-EXTERNAL-ID"] = UserID;
  //   return this.httpClient.post(`${this.MAGIC_BELL_API}/push_subscriptions`, payload, { headers: this.httpHeaders });
  // }

  //#endregion

  //#region PUT

  updateUser(payload: Cls_MBUser) {
    return this.http.put(`${this.MAGIC_BELL_API}/users/${payload.user?.id}`, payload, { headers: this.httpHeaders });
  }

  //#endregion

  //#region DELETE

  deleteNotification(notification_id: string | number) {
    const API = this.httpClient.delete(`${this.MAGIC_BELL_API}/notifications/${notification_id}`, { headers: this.httpHeaders });
    return this.returnModifiedObserver(API);
  }

  //#endregion
}
